import React, { useEffect, useMemo, useRef, useState } from "react";

const feedbackData = [
  {
    name: "Jhon Smith",
    text: "I met someone genuine in a week. The vibe prompts make it effortless.",
    rating: 5,
  },
  {
    name: "Smith Jhon",
    text: "Clean design, great matches, and I feel safe using it every day.",
    rating: 5,
  },
  {
    name: "Devid Jhon",
    text: "The best dating experience I've had. Real connections, zero noise.",
    rating: 5,
  },
  {
    name: "Ava Daniels",
    text: "Verified profiles are a game changer. I finally feel comfortable.",
    rating: 5,
  },
  {
    name: "Maya Lee",
    text: "Matches are so much more aligned with what I’m looking for.",
    rating: 4,
  },
];

function FeedbackCarousel() {
  const trackRef = useRef(null);
  const [cardsPerView, setCardsPerView] = useState(3);
  const [currentIndex, setCurrentIndex] = useState(0);

  const stars = (count) => "⭐".repeat(count);

  const slides = useMemo(() => feedbackData, []);

  useEffect(() => {
    const updateCardsPerView = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setCardsPerView(3);
      } else if (width >= 768) {
        setCardsPerView(2);
      } else {
        setCardsPerView(1);
      }
    };

    updateCardsPerView();
    window.addEventListener("resize", updateCardsPerView);
    return () => window.removeEventListener("resize", updateCardsPerView);
  }, []);

  const getCardMetrics = () => {
    if (!trackRef.current) return { cardWidth: 0, gap: 0 };
    const container = trackRef.current;
    const styles = window.getComputedStyle(container);
    const gap = parseFloat(styles.columnGap || styles.gap || "0");
    const cardWidth =
      (container.clientWidth - gap * (cardsPerView - 1)) / cardsPerView;
    return { cardWidth, gap };
  };

  const scrollToIndex = (nextIndex) => {
    if (!trackRef.current) return;
    const { cardWidth, gap } = getCardMetrics();
    const clampedIndex = Math.max(0, Math.min(nextIndex, maxIndex));
    // Scroll exactly one card at a time: card width + gap
    const left = clampedIndex * (cardWidth + gap);
    trackRef.current.scrollTo({ left, behavior: "smooth" });
    setCurrentIndex(clampedIndex);
  };

  const handleArrowClick = (direction) => {
    scrollToIndex(direction === "next" ? currentIndex + 1 : currentIndex - 1);
  };

  const handleScroll = () => {
    if (!trackRef.current) return;
    const { cardWidth, gap } = getCardMetrics();
    const index = Math.round(
      trackRef.current.scrollLeft / (cardWidth + gap || 1),
    );
    if (index !== currentIndex) {
      setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
    }
  };

  const maxIndex = Math.max(slides.length - cardsPerView, 0);
  const isPrevDisabled = currentIndex <= 0;
  const isNextDisabled = currentIndex >= maxIndex;

  return (
    <section
      id="feedback"
      className="section-neutral py-16 sm:py-20 relative overflow-hidden"
    >
      {/* Left Corner Decorative Flow */}
      <div className="absolute -left-40 -top-20 w-80 h-80 rounded-full bg-gradient-to-br from-pink-200 to-purple-200 opacity-40 blur-3xl"></div>
      <div className="absolute -left-32 top-1/2 w-64 h-64 rounded-full bg-gradient-to-tr from-pink-100 to-transparent opacity-30 blur-2xl"></div>

      {/* Right Corner Decorative Flow */}
      <div className="absolute -right-40 -bottom-20 w-80 h-80 rounded-full bg-gradient-to-br from-purple-200 to-pink-200 opacity-40 blur-3xl"></div>
      <div className="absolute -right-32 top-1/3 w-72 h-72 rounded-full bg-gradient-to-bl from-indigo-100 to-transparent opacity-25 blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-pink-500 font-semibold">
              Feedback
            </p>
            <h2 className="heading-primary text-3xl sm:text-4xl font-bold text-gray-900 mt-3">
              Loved by real people
            </h2>
            <p className="text-lg text-subtle mt-4 max-w-2xl">
              Real stories from people who found meaningful connections on
              Vibely.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleArrowClick("prev")}
              disabled={isPrevDisabled}
              className={`btn-secondary w-11 h-11 rounded-full border border-gray-200 text-gray-700 transition ${
                isPrevDisabled
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-gray-50"
              }`}
              aria-label="Previous testimonials"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => handleArrowClick("next")}
              disabled={isNextDisabled}
              className={`btn-secondary w-11 h-11 rounded-full border border-gray-200 text-gray-700 transition ${
                isNextDisabled
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-gray-50"
              }`}
              aria-label="Next testimonials"
            >
              →
            </button>
          </div>
        </div>

        <div className="relative">
          <div
            ref={trackRef}
            className="feedback-track hide-scrollbar"
            style={{
              "--card-gap": "24px",
              "--cards-per-view": String(cardsPerView),
            }}
            onScroll={handleScroll}
          >
            {slides.map((item, index) => (
              <article
                key={`${item.name}-${index}`}
                className="feedback-card card-hover snap-start rounded-3xl bg-gradient-to-br from-white via-white to-pink-50 border border-pink-100 shadow-lg p-6 sm:p-8"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-white flex items-center justify-center text-xl font-bold">
                    {item.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      {item.name}
                    </p>
                    <p className="text-sm text-yellow-500">
                      {stars(item.rating)}
                    </p>
                  </div>
                </div>
                <p className="text-gray-600 mt-5 leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default FeedbackCarousel;
